import React, {useEffect} from "react"
import {Navigate, Routes, Route, useLocation, Location, useNavigate} from "react-router-dom"
import {LoginCallback} from "@okta/okta-react"
import styled from "styled-components"
import {Tooltip} from "@mui/material"
import BugReportIcon from "@mui/icons-material/BugReport"

import {CustomerPage} from "./CustomerPage"
import {CustomersPage} from "./CustomersPage"
import {DevToolsPage} from "./DevToolsPage"
import {RequiredAuth} from "./RequiredAuth"
import {HomePage} from "./HomePage"

const isDevEnv = () => {
	const env = import.meta.env.VITE_ENV
	return env === "dev" || env === "local"
}

const isEmbedded = () => {
	try {
		return window.self !== window.top
	} catch {
		return true
	}
}

function FrontPages({location}: {location: Location}) {
	return (
		<Routes>
			<Route index element={<HomePage />} />
			<Route path="/customers" element={<CustomersPage location={location} />} />
			<Route path="/customers/:id/*" element={<CustomerPage />} />
			{isDevEnv() && <Route path="/dev-tools/*" element={<DevToolsPage />} />}
		</Routes>
	)
}

function OktaPages({location}: {location: Location}) {
	return (
		<Routes>
			<Route
				path="/login/callback"
				element={<LoginCallback loadingElement={<h1 id="loading-icon">Loading...</h1>} />}
			/>
			<Route path="/" element={<RequiredAuth />}>
				<Route index element={<Navigate to="/customers" />} />
				<Route path="/customers" element={<CustomersPage location={location} />} />
				<Route path="/customers/:id/*" element={<CustomerPage />} />
				{isDevEnv() && <Route path="/dev-tools/*" element={<DevToolsPage />} />}
			</Route>
		</Routes>
	)
}

const Style = styled.div`
	height: 100vh;
`

const DevToolsButton = styled.button`
	position: fixed;
	bottom: 16px;
	right: 16px;
	z-index: 9999;
	width: 40px;
	height: 40px;
	border-radius: 50%;
	border: none;
	background-color: #1976d2;
	color: white;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
	&:hover {
		background-color: #1565c0;
	}
`

function DevToolsFab() {
	const navigate = useNavigate()
	return (
		<Tooltip title="Dev Tools" placement="left">
			<DevToolsButton onClick={() => navigate("/dev-tools")}>
				<BugReportIcon fontSize="small" />
			</DevToolsButton>
		</Tooltip>
	)
}

function Pages() {
	const location = useLocation()

	useEffect(() => {
		localStorage.setItem("lastpage", `${location.pathname}${location.search}`)
	}, [location])

	return (
		<Style>
			{isEmbedded() ? <FrontPages location={location} /> : <OktaPages location={location} />}
			{isDevEnv() && <DevToolsFab />}
		</Style>
	)
}

export {Pages}
